import AppLayout from "@/components/layout/AppLayout";
import SchoolLogs from "@/modules/Activity-Log/SchoolLogs";
import StudentLogs from "@/modules/Activity-Log/StudentLogs";
import Assignments from "@/modules/Assignments";
import AddAssignment from "@/modules/Assignments/pages/AddAssignment";
import Auth from "@/modules/Auth";
import CreatePasswordForm from "@/modules/Auth/components/create-password-form";
import OtpForm from "@/modules/Auth/components/otp-form";
import AuthLayout from "@/modules/Auth/layout/AuthLayout";
import CourseManagement from "@/modules/Course-Management";
import CourseDetail from "@/modules/Course-Management/pages/CourseDetail";
import CreateCourse from "@/modules/Course-Management/pages/CreateCourse";
import EditCourse from "@/modules/Course-Management/pages/EditCourse";
import DashboardPage from "@/modules/Dashboard";
import SchoolDashboardPage from "@/modules/Dashboard/school-dashboard";
import TeacherDashboardPage from "@/modules/Dashboard/teacher-dashboard";
import Feedbacks from "@/modules/Feedbacks";
import PageNotFound from "@/modules/PageNotFound";
import SchoolManagement from "@/modules/School-Management";
import AddSchool from "@/modules/School-Management/pages/AddSchool";
import Submissions from "@/modules/Submissions";
import AddStudent from "@/modules/User-Management/pages/AddStudent";
import AddTeacher from "@/modules/User-Management/pages/AddTeacher";
import AssignStudents from "@/modules/User-Management/pages/AssignStudents";
import StudentManagement from "@/modules/User-Management/pages/StudentHome";
import TeacherManagement from "@/modules/User-Management/pages/TeacherHome";
import MyCourses from "@/modules/Mycourses";
import MyClass from "@/modules/Myclass";
import {
  logout,
  selectIsAuthenticated,
  selectUserRole,
} from "@/store/slices/authSlice";
import {
  CATALOG_ADMIN_ROLES,
  hasRolePermissions,
  ROLE_PERMISSIONS,
  ROLES,
} from "@/utils/constants";
import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { IdleTimerProvider } from "react-idle-timer";
import { AppDispatch } from "@/store";
import GroupSchoolAdmin from "@/modules/Group-School-Admin";
import GroupSchoolAdminDetails from "@/modules/Group-School-Admin/pages/GroupSchoolAdminDetails";
import LandingLayout from "@/modules/Landing/layout/LandingLayout";
import HomePage from "@/modules/Landing/pages/HomePage";
import CategoryListingPage from "@/modules/Landing/pages/CategoryListingPage";
import CourseDetailPage from "@/modules/Landing/pages/CourseDetailPage";
import CheckoutPage from "@/modules/Landing/pages/CheckoutPage";
import PaymentSuccessPage from "@/modules/Landing/pages/PaymentSuccessPage";
import PrivacyPage from "@/modules/Landing/pages/PrivacyPage";
import RefundPolicyPage from "@/modules/Landing/pages/RefundPolicyPage";
import TermsPage from "@/modules/Landing/pages/TermsPage";
import ClassManagement from "@/modules/Class-Management";
import AddClassPage from "@/modules/Class-Management/pages/AddClassPage";
import EditClassPage from "@/modules/Class-Management/pages/EditClassPage";
import ViewClassPage from "@/modules/Class-Management/pages/ViewClassPage";
import ClassOfferingDetailPage from "@/modules/Class-Management/pages/ClassOfferingDetailPage";
import LevelManagementPage from "@/modules/Class-Management/pages/LevelManagementPage";

// Protected route component
const ProtectedRoute: FC<{
  children: React.ReactNode;
  permissions: string[];
  roles?: string[];
}> = ({ children, permissions, roles }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);
  const location = useLocation();

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const handleOnIdle = async () => {
    console.log("User is idle for 12 minutes - logging out");
    // Dispatch the logout action directly from the store
    // We need to create a custom action that doesn't require navigate
    try {
      dispatch(logout(navigate));
    } catch (error) {
      console.log(error);
    }
  };

  if (!isAuthenticated) {
    // Redirect to login page if not authenticated
    return <Navigate to="/auth/signin" state={{ from: location }} replace />;
  }

  if (roles?.length && !roles.includes(userRole?.code ?? "")) {
    return (
      <AppLayout>
        <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
          <div className="flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center">
            <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
              You do not have permission to access this page
            </p>
            <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
              Please contact your administrator for assistance.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (
    !hasRolePermissions(
      userRole?.permissions as string[],
      permissions
    )
  ) {
    console.log(
      "User does not have permission to access this page",
      userRole?.permissions,
      permissions
    );
    // Redirect to dashboard if not authorized
    return (
      <AppLayout>
        <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
          <div
            className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
          >
            <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
              You do not have permission to access this page
            </p>
            <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
              Please contact your administrator for assistance.
            </p>
            {/* <Button
              className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
              onClick={() => navigate("/assignments/add-assignment")}
            >
              Add Assignment
            </Button> */}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <IdleTimerProvider
      crossTab
      timeout={12 * 60 * 1000} // 15 minutes in milliseconds
      onIdle={handleOnIdle} // Function to execute on idle
      debounce={500} // Debounce user activity events to reduce noise
    >
      {children}
    </IdleTimerProvider>
  );
};

// Public route component (redirects to dashboard if already authenticated)
const PublicRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const userRole = useSelector(selectUserRole);

  // Temporarily redirect all users to dashboard until my-courses module is ready
  // Once my-courses module is ready, use the role-based navigation
  // Check if user is a student (using ROLES.STUDENT for future reference)
  const isSchoolAdmin = userRole?.code === ROLES.SCHOOL_ADMIN;
  const isGroupSchoolAdmin = userRole?.code === ROLES.GROUP_SCHOOL_ADMIN;
  const isSuperAdmin = userRole?.code === ROLES.SUPER_ADMIN;
  const isTeacher = userRole?.code === ROLES.TEACHER;
  const isStudent = userRole?.code === ROLES.STUDENT;

  if (isAuthenticated && isSchoolAdmin) {
    // Redirect to dashboard if already authenticated
    return <Navigate to={"/dashboard"} replace />;
  } else if (isAuthenticated && isGroupSchoolAdmin) {
    return <Navigate to={"/dashboard"} replace />;
  } else if (isAuthenticated && isSuperAdmin) {
    return <Navigate to={"/dashboard"} replace />;
  } else if (isAuthenticated && isTeacher) {
    return <Navigate to={"/dashboard"} replace />;
  } else if (isAuthenticated && isStudent) {
    return <Navigate to={"/my-courses"} replace />;
  }

  return <>{children}</>;
};

const RootRedirect: FC = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);
  const homeRoute =
    userRole?.code === ROLES.STUDENT ? "/my-courses" : "/dashboard";

  if (!isAuthenticated) {
    return <Navigate to="/auth/signin" replace />;
  }

  return <Navigate to={homeRoute} replace />;
};

const AppRoutes: FC = () => {
  const userRole = useSelector(selectUserRole);

  return (
    <Routes>
      {/* Public landing site — browsable without login */}
      <Route element={<LandingLayout />}>
        <Route path="/classes" element={<HomePage />} />
        <Route path="/classes/level/:levelId" element={<CategoryListingPage />} />
        <Route
          path="/classes/level/:levelId/:classId"
          element={<CourseDetailPage />}
        />
        <Route path="/classes/:categorySlug" element={<CategoryListingPage />} />
        <Route
          path="/classes/:categorySlug/:courseSlug"
          element={<CourseDetailPage />}
        />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<PaymentSuccessPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refund" element={<RefundPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Route>

      {/* Public routes */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/auth">
        <Route
          path="signin"
          element={
            <PublicRoute>
              <Auth />
            </PublicRoute>
          }
        />
        <Route
          path="otp"
          element={
            <PublicRoute>
              <AuthLayout>
                <OtpForm />
              </AuthLayout>
            </PublicRoute>
          }
        />
        <Route
          path="generate-password"
          element={
            <PublicRoute>
              <AuthLayout>
                <CreatePasswordForm />
              </AuthLayout>
            </PublicRoute>
          }
        />
      </Route>

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute permissions={[ROLE_PERMISSIONS.TAB_DASHBOARD]}>
            {(userRole?.code === ROLES.SCHOOL_ADMIN || userRole?.code === ROLES.GROUP_SCHOOL_ADMIN) ? (
              <SchoolDashboardPage />
            ) : userRole?.code === ROLES.TEACHER ? (
              <TeacherDashboardPage />
            ) : (
              <DashboardPage />
            )}
          </ProtectedRoute>
        }
      />

      <Route path="/school-management">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_SCHOOL_MANAGEMENT]}
            >
              <AppLayout>
                <SchoolManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="add-school"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_SCHOOL_MANAGEMENT]}
            >
              <AppLayout>
                <AddSchool />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="view-school/:id"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_SCHOOL_MANAGEMENT]}
            >
              <AppLayout>
                <AddSchool viewMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="edit-school/:id"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_SCHOOL_MANAGEMENT]}
            >
              <AppLayout>
                <AddSchool editMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="/user-management/student">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.ACTION_VIEW_STUDENT]}
            >
              <AppLayout>
                <StudentManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="add-student"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.BUTTON_ADD_STUDENT]}
            >
              <AppLayout>
                <AddStudent />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="view-student/:id"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
              ]}
            >
              <AppLayout>
                <AddStudent viewMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="edit-student/:id"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
              ]}
            >
              <AppLayout>
                <AddStudent editMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="/user-management/teacher">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.ACTION_VIEW_TEACHER]}
            >
              <AppLayout>
                <TeacherManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="add-teacher"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.BUTTON_ADD_TEACHER]}
            >
              <AppLayout>
                <AddTeacher />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="view-teacher/:id"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.ACTION_VIEW_TEACHER]}
            >
              <AppLayout>
                <AddTeacher viewMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="edit-teacher/:id"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.ACTION_EDIT_TEACHER]}
            >
              <AppLayout>
                <AddTeacher editMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="assign-student"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.ACTION_ASSIGN_STUDENTS]}
            >
              <AppLayout>
                <AssignStudents />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="/course-management">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT]}
            >
              <AppLayout>
                <CourseManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="create"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.BUTTON_CREATE_COURSE,
                ROLE_PERMISSIONS.ACTION_EDIT_COURSE,
              ]}
              roles={[ROLES.SUPER_ADMIN]}
            >
              <AppLayout contentClassName="px-0">
                <CreateCourse />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="edit/:id"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.ACTION_EDIT_COURSE]}
              roles={[ROLES.SUPER_ADMIN]}
            >
              <AppLayout contentClassName="px-0">
                <EditCourse />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="view-course/:id"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_MY_COURSES,
                ROLE_PERMISSIONS.TAB_MY_CLASS,
              ]}
            >
              <AppLayout contentClassName="px-0">
                <CourseDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="/class-management">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
              ]}
              roles={CATALOG_ADMIN_ROLES}
            >
              <AppLayout>
                <ClassManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="create"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
              ]}
              roles={CATALOG_ADMIN_ROLES}
            >
              <AppLayout contentClassName="px-0">
                <AddClassPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="edit/:id"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
              ]}
              roles={CATALOG_ADMIN_ROLES}
            >
              <AppLayout contentClassName="px-0">
                <EditClassPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="view/:id"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
              ]}
              roles={CATALOG_ADMIN_ROLES}
            >
              <AppLayout contentClassName="px-0">
                <ViewClassPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="detail/:id"
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_ASSIGNMENT_MANAGEMENT,
                ROLE_PERMISSIONS.TAB_MY_COURSES,
              ]}
            >
              <AppLayout contentClassName="px-0">
                <ClassOfferingDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="/level-management"
        element={
          <ProtectedRoute
            permissions={[
              ROLE_PERMISSIONS.TAB_CLASS_MANAGEMENT,
              ROLE_PERMISSIONS.TAB_COURSE_MANAGEMENT,
            ]}
            roles={CATALOG_ADMIN_ROLES}
          >
            <AppLayout>
              <LevelManagementPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route path="/assignments">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[
                ROLE_PERMISSIONS.TAB_ASSIGNMENT_MANAGEMENT
              ]}
            >
              <AppLayout>
                <Assignments />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="add-assignment"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_ASSIGNMENT_MANAGEMENT]}
            >
              <AppLayout>
                <AddAssignment />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="/submissions">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_SUBMISSION_MANAGEMENT]}
            >
              <AppLayout>
                <Submissions />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="/student-log"
        element={
          <ProtectedRoute permissions={[ROLE_PERMISSIONS.TAB_STUDENT_LOG]}>
            <AppLayout>
              <StudentLogs />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/activity/student-logs"
        element={
          <ProtectedRoute permissions={[ROLE_PERMISSIONS.TAB_ACTIVITY_STUDENT]}>
            <AppLayout>
              <StudentLogs />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity/school-logs"
        element={
          <ProtectedRoute permissions={[ROLE_PERMISSIONS.TAB_ACTIVITY_SCHOOL]}>
            <AppLayout>
              <SchoolLogs />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-courses"
        element={
          <ProtectedRoute permissions={[ROLE_PERMISSIONS.TAB_MY_COURSES]}>
            <AppLayout>
              <MyCourses />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-class"
        element={
          <ProtectedRoute permissions={[ROLE_PERMISSIONS.TAB_MY_CLASS]}>
            <AppLayout>
              <MyClass />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Fallback route */}
      <Route path="/feedbacks">
        <Route
          index
          element={
            <ProtectedRoute permissions={[ROLE_PERMISSIONS.TAB_FEEDBACK]}>
              <AppLayout>
                <Feedbacks />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Group School Admin route */}
      <Route path="/group-school-admin">
        <Route
          index
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_GROUP_SCHOOL_ADMIN]}
            >
              <AppLayout>
                <GroupSchoolAdmin />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="add"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_GROUP_SCHOOL_ADMIN]}
            >
              <AppLayout>
                <GroupSchoolAdminDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="view/:id"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_GROUP_SCHOOL_ADMIN]}
            >
              <AppLayout>
                <GroupSchoolAdminDetails viewMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="edit/:id"
          element={
            <ProtectedRoute
              permissions={[ROLE_PERMISSIONS.TAB_GROUP_SCHOOL_ADMIN]}
            >
              <AppLayout>
                <GroupSchoolAdminDetails editMode />
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="/page-not-found" element={<PageNotFound />} />

      <Route path="*" element={<Navigate to="/page-not-found" replace />} />
    </Routes>
  );
};

export default AppRoutes;
